/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import PropTypes from 'prop-types';
import { toLocal } from '../../utils/dateTime';

const formatTimestamp = (isoString) => {
  try {
    return toLocal(isoString, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return isoString;
  }
};

// Read-only notes preview shown at the top of Review/ReviewShell — a warning
// when *this* user (per canManage, matched server-side on name+email) hasn't
// added a note yet, even if others have, plus a scrollable preview of
// whatever notes exist. View/Add hand off to the same NotesModal instance
// Capture.jsx already manages (via onView/onAdd), just opened in a different
// mode. showWarning is off for ResumeGate — that screen is a read-only
// hand-off preview, not a point where the user is expected to add a note.
// The "no notes at all" message below is independent of showWarning — even
// on a read-only screen the View/Add buttons need context, otherwise they
// render with nothing explaining what they refer to.
const NotesSummary = ({ notes, onView, onAdd, showWarning = true }) => {
  const hasOwnNote = notes.some((note) => note.canManage);
  const hasNotes = notes.length > 0;

  return (
    <div className="mb-6">
      {!hasNotes && (
        <div className="mb-3 p-3 bg-warning/10 border border-warning/20 rounded-xl text-[13px] font-semibold text-warning">
          No notes have been added yet.
        </div>
      )}
      {hasNotes && showWarning && !hasOwnNote && (
        <div className="mb-3 p-3 bg-warning/10 border border-warning/20 rounded-xl text-[13px] font-semibold text-warning">
          You haven't added a note yet.
        </div>
      )}
      {notes.length > 0 && (
        <>
          <h2 className="text-[16px] font-black text-base-content mb-2">Notes ({notes.length})</h2>
          <div className="max-h-40 overflow-y-auto space-y-3 bg-base-200 rounded-xl p-3 mb-3">
            {notes.map((note, index) => (
              <div key={note.id} className={index < notes.length - 1 ? 'pb-2 border-b border-base-300/50' : ''}>
                <p className="text-[13px] font-bold text-base-content truncate">{note.name}</p>
                <p className="text-[14px] text-base-content custom-line-break">{note.note}</p>
                <p className="text-[11px] italic text-base-content/50">{formatTimestamp(note.timestamp)}</p>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="flex justify-center gap-3">
        <button
          type="button"
          onClick={onView}
          className="h-[42px] py-3 px-10 font-bold text-[17px] rounded-[8px] border-2 active:scale-[0.98] transition-all shadow-md flex items-center justify-center bg-black text-white border-black"
        >
          View
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="h-[42px] py-3 px-10 font-bold text-[17px] rounded-[8px] border-2 active:scale-[0.98] transition-all shadow-md flex items-center justify-center bg-primary text-primary-content border-primary"
        >
          Add
        </button>
      </div>
    </div>
  );
};

export default NotesSummary;

NotesSummary.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      note: PropTypes.string,
      timestamp: PropTypes.string,
      canManage: PropTypes.bool,
    })
  ),
  onView: PropTypes.func,
  onAdd: PropTypes.func,
  showWarning: PropTypes.bool,
};

NotesSummary.defaultProps = {
  notes: [],
};
